import { ChevronDown, ChevronRight } from "lucide-react";
import { SegmentedControl } from "@shared/ui/SegmentedControl";
import { TextInput } from "@shared/ui/TextInput";
import { Textarea } from "@shared/ui/Textarea";
import type {
    Discretion,
    ElementSetting,
    LeftoverRows,
    Overflow,
} from "@entities/generation-setting";
import type { ShapeKind } from "@features/office";

/**
 * 行が要る図形の情報。名前と種別しか使わないので、読み取り側の型に縛られない。
 * 呼び手が `ReconciledShape` を渡しても通る。
 */
export type ElementRowShape = {
    /** 表示名。settings に無ければ図形名が入る。 */
    name: string;
    /** 図形名。名前を空にしたときの戻り先を示すために持つ。 */
    shapeName: string;
    kind: ShapeKind;
};

/**
 * 種別バッジの表示名。読み手の見当付けに使う。
 *
 * **「その他」は本当に分からないものだけに使う。** 図形として描いた枠を「その他」と呼んでいた
 * ため、実物のフォーマットでは 7 割の行が同じ呼び名になっていた（記録 70）。
 */
const KIND_LABEL: Record<ShapeKind, string> = {
    body: "本文",
    table: "表",
    chart: "グラフ",
    diagram: "図解",
    image: "画像",
    shape: "図形",
    group: "グループ",
    line: "線",
    other: "その他",
};

const DISCRETION_ITEMS = [
    { id: "embed", label: "埋め込み" },
    { id: "compose", label: "創造" },
    { id: "propose", label: "提案" },
];

const OVERFLOW_ITEMS = [
    { id: "fit", label: "収める" },
    { id: "other", label: "その他へ" },
    { id: "grow", label: "行を増やす" },
    { id: "nextSlide", label: "次の枚へ" },
];

const LEFTOVER_ITEMS = [
    { id: "keep", label: "残す" },
    { id: "delete", label: "削除する" },
];

type Props = {
    shape: ElementRowShape;
    setting: ElementSetting | undefined;
    expanded: boolean;
    /** PowerPoint 側でいま選択されている図形。行を目立たせ、開いた状態にする。 */
    selected?: boolean;
    onToggle: () => void;
    onChange: (next: ElementSetting) => void;
};

/**
 * 画面B の要素 1 行。畳んだときは名前と裁量だけを出し、開くとフォーマットの決め事を並べる。
 *
 * 名前は settings が正本で、ここで編集できる。実データの図形名は自動採番が大半なので、
 * これを直せないとメンションの候補が使い物にならない。`PowerPoint.Shape.name` は
 * 書き換えない（意図せぬファイル改変を避けるため）。
 * 「入りきらないとき」と「余った行」は表の枠にだけ出す。ほかの枠では意味を持たないため。
 * 「足してよい行数」は「行を増やす」のときだけ出す（空欄は自動。超えたら次の枚へ送る）。
 */
export function ElementRow({ shape, setting, expanded, selected, onToggle, onChange }: Props) {
    const discretion: Discretion = setting?.discretion ?? "embed";
    const isTable = shape.kind === "table";

    function patch(next: Partial<ElementSetting>): void {
        onChange({
            ...setting,
            ...next,
            updatedAt: new Date().toISOString(),
            updatedBy: "",
        });
    }

    /**
     * 足してよい行数の入力。空欄は「自動」に戻し、数字だけの文字は 0 以上の整数として保存する。
     * 数字以外が混ざる入力は受け付けない（欄の値が保存値のままなので、打っても何も変わらない）。
     */
    function patchMaxRowsBeforeNext(raw: string): void {
        const text = raw.trim();
        if (text === "") {
            patch({ maxRowsBeforeNext: undefined });
        } else if (/^\d+$/.test(text)) {
            patch({ maxRowsBeforeNext: Number.parseInt(text, 10) });
        }
    }

    const rowClass = selected ? "row-selected" : "";

    return (
        <div className={`border-t border-text-primary/8 first:border-t-0 ${rowClass}`}>
            <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left"
                onClick={onToggle}
            >
                <span className="shrink-0 rounded bg-sidebar-bg px-1.5 text-[11px] font-semibold text-text-secondary">
                    {KIND_LABEL[shape.kind]}
                </span>
                <span
                    className={`min-w-0 flex-1 truncate text-[13px] ${selected ? "font-semibold" : ""}`}
                >
                    {shape.name}
                </span>
                {selected && <span className="shrink-0 text-[11px] text-brand-dark">選択中</span>}
                {!expanded && (
                    <span className="shrink-0 text-xs text-text-secondary">
                        {DISCRETION_ITEMS.find((i) => i.id === discretion)?.label}
                    </span>
                )}
                <span className="flex shrink-0 text-text-muted">
                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </span>
            </button>

            {expanded && (
                <div className="flex flex-col gap-2 px-3 pb-3">
                    <label className="block">
                        <span className="block text-[11px] text-text-muted">名前</span>
                        <TextInput
                            value={setting?.name ?? ""}
                            onChange={(v) => patch({ name: v })}
                            fullWidth
                            placeholder={shape.shapeName}
                        />
                    </label>

                    <label className="block">
                        <span className="block text-[11px] text-text-muted">ねらい</span>
                        <Textarea
                            value={setting?.aim ?? ""}
                            onChange={(v) => patch({ aim: v })}
                            rows={2}
                            placeholder="この枠が何を担うか"
                        />
                    </label>

                    <label className="block rounded border border-dashed border-border-subtle p-2">
                        <span className="block text-[11px] text-text-muted">作成ルール</span>
                        <Textarea
                            value={setting?.rule ?? ""}
                            onChange={(v) => patch({ rule: v })}
                            rows={2}
                            placeholder="書式と表現の要件（任意）"
                        />
                    </label>

                    <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 text-xs text-text-secondary">裁量</span>
                        <SegmentedControl
                            items={DISCRETION_ITEMS}
                            value={discretion}
                            onChange={(v) => patch({ discretion: v as Discretion })}
                        />
                    </div>

                    {isTable && (
                        <>
                            <div>
                                <span className="block text-xs text-text-secondary">
                                    入りきらないとき
                                </span>
                                <div className="mt-1">
                                    <SegmentedControl
                                        items={OVERFLOW_ITEMS}
                                        value={setting?.overflow ?? "fit"}
                                        onChange={(v) => patch({ overflow: v as Overflow })}
                                    />
                                </div>
                            </div>
                            {setting?.overflow === "grow" && (
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="min-w-0 flex-1 text-xs text-text-secondary">
                                            足してよい行数
                                        </span>
                                        <TextInput
                                            // 空欄は「自動」で、値が無いこと自体が正しい状態なので、
                                            // 入力途中の下書きは持たず保存値をそのまま映す。
                                            value={
                                                setting.maxRowsBeforeNext === undefined
                                                    ? ""
                                                    : String(setting.maxRowsBeforeNext)
                                            }
                                            onChange={(v) => patchMaxRowsBeforeNext(v)}
                                            size="sm"
                                            className="w-14"
                                            ariaLabel="足してよい行数"
                                            placeholder="自動"
                                        />
                                        <span className="shrink-0 text-[11px] text-text-muted">
                                            行
                                        </span>
                                    </div>
                                    <span className="mt-0.5 block text-[11px] text-text-muted">
                                        超えると次の枚へ送る。空欄は表の下の余白から自動で決める。
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <span className="min-w-0 flex-1 text-xs text-text-secondary">
                                    余った行
                                </span>
                                <SegmentedControl
                                    items={LEFTOVER_ITEMS}
                                    value={setting?.leftoverRows ?? "keep"}
                                    onChange={(v) => patch({ leftoverRows: v as LeftoverRows })}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
